/**
 * External dependencies
 */
import {
	withBlockStyles,
	withSetAttributeHook,
	withTabbedInspector,
	withUniqueClass,
	withClickOpenInspector,
} from '~cartzillagb/higher-order'

import {
	createButtonAttributeNames,
	createResponsiveAttributeNames,
	createTypographyAttributeNames,
} from '~cartzillagb/util'

import {
	BlockContainer,
	AdvancedRangeControl,
	HeadingButtonsControl,
	PanelAdvancedSettings,
	PanelSpacingBody,
	ResponsiveControl,
	ButtonControlsHelper,
	ButtonEditHelper,
} from '~cartzillagb/components'

import { i18n } from 'cartzillagb'
import classnames from 'classnames'
import createStyles from './style'
import { showOptions } from './util'

/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n'
import { Fragment } from '@wordpress/element'
import { addFilter, applyFilters } from '@wordpress/hooks'
import { InnerBlocks, RichText } from '@wordpress/block-editor'
import {
	PanelBody, ToggleControl
} from '@wordpress/components'
import { compose } from '@wordpress/compose'

addFilter( 'cartzillagb.products-with-header.edit.inspector.style.before', 'cartzillagb/products-with-header', ( output, props ) => {
	const { attributes, setAttributes } = props
	const {
		enableContainer,

		// Title.
		showTitle = true,
		titleTag = '',

		// Button.
		showHeaderButton = false,
		showFooterButton = false,
	} = attributes

	const show = showOptions( props )

	return (
		<Fragment>
			<PanelBody title={ __( "General Settings", i18n ) } initialOpen={ true     }>
				<ToggleControl
					label={ __( 'Enable Container', i18n ) }
					checked={ enableContainer }
					onChange={ enableContainer => setAttributes( { enableContainer } ) }
				/>
			</PanelBody>
			<PanelAdvancedSettings
				title={ __( 'Title', i18n ) }
				id="title"
				checked={ showTitle }
				onChange={ showTitle => setAttributes( { showTitle } ) }
				toggleOnSetAttributes={ [
					...createTypographyAttributeNames( 'title%s' ),
					'titleTag',
				] }
				toggleAttributeName="showTitle"
			>
				<HeadingButtonsControl
					value={ titleTag || 'h3' }
					onChange={ titleTag => setAttributes( { titleTag } ) }
				/>
			</PanelAdvancedSettings>
			<PanelAdvancedSettings
				title={ __( 'Header Button', i18n ) }
				id="header-button"
				checked={ showHeaderButton }
				onChange={ showHeaderButton => setAttributes( { showHeaderButton } ) }
				toggleOnSetAttributes={ [
					...createButtonAttributeNames( 'headerButton%s' ),
					...createResponsiveAttributeNames( 'headerButton%sAlign' ),
				] }
				toggleAttributeName="showHeaderButton"
			>
				<ButtonControlsHelper
					attrNameTemplate="headerButton%s"
					setAttributes={ setAttributes }
					blockAttributes={ props.attributes }
				/>
			</PanelAdvancedSettings>
			<PanelAdvancedSettings
				title={ __( 'Footer Button', i18n ) }
				id="footer-button"
				checked={ showFooterButton }
				onChange={ showFooterButton => setAttributes( { showFooterButton } ) }
				toggleOnSetAttributes={ [
					...createButtonAttributeNames( 'footerButton%s' ),
					...createResponsiveAttributeNames( 'footerButton%sAlign' ),
				] }
				toggleAttributeName="showFooterButton"
			>
				<ButtonControlsHelper
					attrNameTemplate="footerButton%s"
					setAttributes={ setAttributes }
					blockAttributes={ props.attributes }
				/>
			</PanelAdvancedSettings>
			{ ( show.titleSpacing || show.headerSpacing || show.footerSpacing ) &&
				<PanelSpacingBody
					initialOpen={ false }
					blockProps={ props }
				>
					{ ( show.titleSpacing || show.headerSpacing ) &&
						<ResponsiveControl
							attrNameTemplate="header%sBottomMargin"
							setAttributes={ setAttributes }
							blockAttributes={ props.attributes }
						>
							<AdvancedRangeControl
								label={ show.headerSpacing ? __( 'Header', i18n ) : __( 'Title', i18n ) }
								min={ -50 }
								max={ 100 }
								allowReset={ true }
								className="czgb--help-tip-spacing-header"
							/>
						</ResponsiveControl>
					}
					{ show.footerSpacing &&
						<ResponsiveControl
							attrNameTemplate="footer%sTopMargin"
							setAttributes={ setAttributes }
							blockAttributes={ props.attributes }
						>
							<AdvancedRangeControl
								label={ __( 'Footer', i18n ) }
								min={ -50 }
								max={ 100 }
								allowReset={ true }
								className="czgb--help-tip-spacing-footer"
							/>
						</ResponsiveControl>
					}
				</PanelSpacingBody>
			}
		</Fragment>
	)
} )

const edit = props => {
	const {
		className,
		attributes,
		setAttributes
	} = props
 
	const {
		uniqueClass,
		enableContainer,

		// Title.
		showTitle = true,
		title = '',
		titleTag = 'h3',

		// Button.
		showHeaderButton = false,
		showFooterButton = true,
	} = attributes

	const mainClasses = classnames( [
		'czgb-products-with-header-block',
		className,
		enableContainer ? 'container' : '',
	], applyFilters( 'cartzillagb.products-with-header.main-block.classes', {}, props ) )

	const titleComp = showTitle &&
		<RichText
			tagName={ titleTag || 'h3' }
			value={ title }
			className={ classnames( [
				'czgb-products-with-header__title',
				showHeaderButton ? 'h3 mb-0 pt-3 mr-2' : 'h3 text-center',
			] ) }
			placeholder={ __( 'Title for This Block', i18n ) }
			onChange={ title => setAttributes( { title } ) }
			keepPlaceholderOnFocus
		/>

	const headerButtonComp = showHeaderButton &&
		<ButtonEditHelper
			attrNameTemplate="headerButton%s"
			setAttributes={ setAttributes }
			blockAttributes={ props.attributes }
		/>

	const footerButtonComp = showFooterButton &&
		<ButtonEditHelper
			attrNameTemplate="footerButton%s"
			setAttributes={ setAttributes }
			blockAttributes={ props.attributes }
		/>

	const headerComp = showTitle && showHeaderButton ? (
		<div className="czgb-products-with-header__header d-flex flex-wrap justify-content-between align-items-center pt-1 border-bottom pb-4 mb-4" >
			{ titleComp }
			<div className="pt-3" >
				{ headerButtonComp }
			</div>
		</div>
	) : ( showTitle ? (
		titleComp
	) : ( showHeaderButton ? (
		<div className="czgb-products-with-header__header d-flex flex-wrap justify-content-between align-items-center pt-1 border-bottom pb-4 mb-4" >
			{ headerButtonComp }
		</div>
	) : '' ) )

	const footerComp = showFooterButton && (
		<div className="czgb-products-with-header__footer text-center pt-3" >
			{ footerButtonComp }
		</div>
	)

	const wraperAttr = {}

	if( ! showHeaderButton ) {
		wraperAttr.className = 'pt-4'
	}

	return (
		<BlockContainer.Edit className={ mainClasses } blockProps={ { ...props, attributes:{ ...props.attributes, anchor: uniqueClass } } } render={ () => (
			<Fragment>
				{ headerComp }
				<div { ...wraperAttr }>
					<InnerBlocks
						template={ [ [ 'czgb/products', {} ] ] }
						templateLock="all"
					/>
				</div>
				{ footerComp }
			</Fragment>
		) } />
	)
}

export default compose(
	withUniqueClass,
	withSetAttributeHook,
	withTabbedInspector( [ 'style', 'advanced' ] ),
	withBlockStyles( createStyles, { editorMode: true } ),
	withClickOpenInspector( [
		[ '.czgb-products-with-header__title', 'title' ],
	] )
)( edit )